import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "@effect/platform";
import * as S from "effect/Schema";
import { RiskLanguage } from "./admin-risk-templates.js";
import { FileRow } from "./files.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { InstantFromString, PlainDateFromString } from "./temporal.js";
/**
 * The onsite-inspection offline-first surface: `onsite_inspections`,
 * `inspection_question_answers`, `inspection_answer_risks`, and
 * `onsite_inspection_questionnaire_links` — the four tables
 * apps/web's IndexedDB sync engine queues writes for. Client-scoped like
 * `admin-risk-instances`: every read and write re-checks `Principal`
 * against the row's own `client_id`.
 *
 * Template pulls (questionnaires, risk templates, measure templates the
 * offline cache refreshes) ride the existing `admin-questionnaires`/
 * `admin-risk-templates`/`admin-measure-templates` list/get intents — the
 * offline cache refresh composes those, it does not need an
 * inspections-specific read model.
 *
 * `risk_instances` themselves (and their tag/law/measure-link/attachment
 * link tables) stay on `admin-risk-instances`'s own CRUD — the dual-language
 * creation dance that group already owns is not re-derived here. This
 * group's upsert intents cover only the four inspection-native tables; a
 * risk instance created mid-inspection is created via
 * `admin-risk-instances.createRiskInstance` first (returning the real
 * `translation_group_id` immediately), then referenced by id in an
 * `answer-risks` upsert.
 */
export const InspectionType = S.Literal("safety", "health");
export const InspectionStatus = S.Literal("draft", "completed", "submitted");
export const InspectionAnswerValue = S.Literal("ok", "not_ok", "not_applicable");
/**
 * A contributor as `onsite_inspections.contributors` actually stores them.
 * Only the names are guaranteed: an externally-typed contributor carries no
 * account, so `email` and `company` are absent on rows the legacy UI wrote,
 * and every consumer already treats both as optional (`contributor.email ||
 * ""`, `contributor.company && …`). Declaring them required made any read
 * that encodes such a row fail the whole response — the client's inspection
 * list, a single inspection, and the sync engine's watermark pull.
 */
export const InspectionContributor = S.Struct({
    /**
     * Not a UUID. Three shapes occur: a `yp_users.id` for an account-backed
     * contributor, `external-<uuid>` for one captured in the edit modal (see
     * `edit-inspection-modal.tsx`, which mints exactly that), and `custom_<ms>`
     * for the check-in flow's temporary ids that reached storage. It is an
     * opaque handle the UI matches selections against, never a foreign key.
     */
    id: S.optional(S.String),
    first_name: S.String,
    last_name: S.String,
    email: S.optional(S.String),
    role: S.optional(S.String),
    client_id: S.optional(S.UUID),
    company: S.optional(S.String),
    official_title: S.optional(S.String),
});
export const InspectionReportRecipient = S.Struct({
    email: S.String,
    name: S.String,
});
export const InspectionNotFound = S.Struct({
    _tag: S.Literal("InspectionNotFound"),
});
export const InspectionAnswerNotFound = S.Struct({
    _tag: S.Literal("InspectionAnswerNotFound"),
});
export const InspectionAnswerRiskNotFound = S.Struct({
    _tag: S.Literal("InspectionAnswerRiskNotFound"),
});
export const InspectionQuestionnaireLinkNotFound = S.Struct({
    _tag: S.Literal("InspectionQuestionnaireLinkNotFound"),
});
// ---------------------------------------------------------------------------
// onsite_inspections
// ---------------------------------------------------------------------------
export const OnsiteInspectionRow = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    location_id: S.NullOr(S.UUID),
    inspection_date: S.String,
    inspection_end_date: S.NullOr(S.String),
    type: InspectionType,
    reason: S.NullOr(S.String),
    status: InspectionStatus,
    contributors: S.NullOr(S.Array(InspectionContributor)),
    report_recipients: S.NullOr(S.Array(InspectionReportRecipient)),
    metadata: S.NullOr(S.Unknown),
    created_by: S.NullOr(S.UUID),
    language: RiskLanguage,
    translation_group_id: S.NullOr(S.Int),
    start_time: S.NullOr(S.String),
    end_time: S.NullOr(S.String),
    date_created: S.String,
    date_updated: S.String,
    /** Advances on every write to the row; an update names the one it read. */
    version: S.Int,
});
/**
 * A versioned update whose `base_version` is not the stored version. The row
 * is unchanged; `current` is what the server holds, so the client can tell a
 * write that already landed from one that conflicts with another.
 */
export const InspectionVersionConflict = S.Struct({
    _tag: S.Literal("InspectionVersionConflict"),
    current: OnsiteInspectionRow,
});
export const ListInspectionsParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    location_id: S.optional(S.UUID),
    status: S.optional(InspectionStatus),
    type: S.optional(InspectionType),
    language: S.optional(RiskLanguage),
    date_from: S.optional(PlainDateFromString),
    date_to: S.optional(PlainDateFromString),
    search: S.optional(S.String),
}));
export const ListInspections = HttpApiEndpoint.get("listInspections", "/admin/inspections")
    .setUrlParams(ListInspectionsParams)
    .addSuccess(pagedResponse(OnsiteInspectionRow))
    .addError(Forbidden, { status: 403 });
export const GetInspectionParams = S.Struct({ id: S.UUID });
export const GetInspection = HttpApiEndpoint.get("getInspection", "/admin/inspections/get")
    .setUrlParams(GetInspectionParams)
    .addSuccess(S.Struct({ data: OnsiteInspectionRow }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const UploadInspectionPhotoParams = S.Struct({
    inspection_id: S.UUID,
    filename: S.NonEmptyString,
    content_type: S.NonEmptyString,
    /**
     * A client-minted key stable across retries of the same offline photo.
     * The database enforces uniqueness within the client. Sequential retries
     * and overlapping uploads return the winning file without storing duplicates.
     */
    idempotency_key: S.optional(S.UUID),
});
/**
 * Stores a photo in the client space owned by an inspection the caller may
 * access. The caller names only the inspection; its client and location are
 * fetched and authorized server-side, so a client-less internal inspector can
 * upload without gaining a caller-controlled cross-client write.
 */
export const UploadInspectionPhoto = HttpApiEndpoint.post("uploadInspectionPhoto", "/admin/inspections/photo/upload")
    .setUrlParams(UploadInspectionPhotoParams)
    .setPayload(HttpApiSchema.Uint8Array())
    .addSuccess(S.Struct({ data: FileRow }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * The id a create asked for belongs to another client's row. Nothing was
 * written, and the refusal says nothing about the row that holds the id. The
 * caller mints a new id and tries again.
 */
export const InspectionIdTaken = S.Struct({
    _tag: S.Literal("InspectionIdTaken"),
});
export const CreateInspectionPayload = S.Struct({
    /**
     * The inspection's id, minted by the device that creates it and kept for the
     * life of the inspection, so an inspection created offline is the same row
     * under the same id before and after it syncs. The create is idempotent on
     * it: an id that belongs to a row of the same client returns that row, as it
     * stands now, with `replayed: true`, and writes nothing. The payload is not
     * compared; the caller reads the row and decides. An id under another client
     * is refused as `InspectionIdTaken`. A create never overwrites a row.
     */
    id: S.UUID,
    client_id: S.UUID,
    location_id: S.optional(S.UUID),
    inspection_date: S.String,
    inspection_end_date: S.optional(PlainDateFromString),
    type: InspectionType,
    reason: S.optional(S.String),
    status: S.optionalWith(InspectionStatus, { default: () => "draft" }),
    contributors: S.optional(S.Array(InspectionContributor)),
    report_recipients: S.optional(S.Array(InspectionReportRecipient)),
    metadata: S.optional(S.Unknown),
    language: RiskLanguage,
    translation_group_id: S.optional(S.Int),
    start_time: S.optional(S.String),
    end_time: S.optional(S.String),
});
export const CreateInspection = HttpApiEndpoint.post("createInspection", "/admin/inspections/create")
    .setPayload(CreateInspectionPayload)
    .addSuccess(S.Struct({ data: OnsiteInspectionRow, replayed: S.Boolean }))
    .addError(InspectionIdTaken, { status: 409 })
    .addError(Forbidden, { status: 403 });
const UpdateInspectionPayloadFields = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    /** The version the client's edit was based on. */
    base_version: S.optional(S.Int),
    location_id: S.optional(S.NullOr(S.UUID)),
    inspection_date: S.optional(S.String),
    inspection_end_date: S.optional(S.NullOr(PlainDateFromString)),
    type: S.optional(InspectionType),
    reason: S.optional(S.NullOr(S.String)),
    status: S.optional(InspectionStatus),
    contributors: S.optional(S.NullOr(S.Array(InspectionContributor))),
    report_recipients: S.optional(S.NullOr(S.Array(InspectionReportRecipient))),
    metadata: S.optional(S.NullOr(S.Unknown)),
    start_time: S.optional(S.NullOr(S.String)),
    end_time: S.optional(S.NullOr(S.String)),
});
export const UpdateInspectionPayload = S.transform(UpdateInspectionPayloadFields, S.typeSchema(UpdateInspectionPayloadFields), {
    strict: true,
    decode: (payload) => ({
        ...payload,
        base_version: payload.base_version ?? 0,
    }),
    encode: (payload) => payload,
});
export const UpdateInspection = HttpApiEndpoint.post("updateInspection", "/admin/inspections/update")
    .setPayload(UpdateInspectionPayload)
    .addSuccess(S.Struct({ data: OnsiteInspectionRow }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(InspectionVersionConflict, { status: 409 })
    .addError(Forbidden, { status: 403 });
/** Answers and questionnaire links cascade through inspection_id. Reports protect the group. */
export const InspectionHasReport = S.Struct({
    _tag: S.Literal("InspectionHasReport"),
});
export const InspectionHasReportHistory = S.Struct({
    _tag: S.Literal("InspectionHasReportHistory"),
});
export const DeleteInspectionsByTranslationGroupPayload = S.Struct({
    client_id: S.UUID,
    translation_group_id: S.Int,
});
export const DeleteInspectionsByTranslationGroup = HttpApiEndpoint.post("deleteInspectionsByTranslationGroup", "/admin/inspections/delete-by-translation-group")
    .setPayload(DeleteInspectionsByTranslationGroupPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Int }) }))
    .addError(Forbidden, { status: 403 })
    .addError(InspectionHasReport, { status: 409 })
    .addError(InspectionHasReportHistory, { status: 409 });
// ---------------------------------------------------------------------------
// onsite_inspection_questionnaire_links
// ---------------------------------------------------------------------------
export const InspectionQuestionnaireLinkRow = S.Struct({
    id: S.Int,
    inspection_id: S.UUID,
    questionnaire_translation_group_id: S.Int,
    date_created: S.String,
});
export const ListInspectionQuestionnaireLinksParams = S.Struct({
    inspection_id: S.UUID,
});
export const ListInspectionQuestionnaireLinks = HttpApiEndpoint.get("listInspectionQuestionnaireLinks", "/admin/inspections/questionnaire-links")
    .setUrlParams(ListInspectionQuestionnaireLinksParams)
    .addSuccess(S.Struct({ items: S.Array(InspectionQuestionnaireLinkRow) }))
    .addError(Forbidden, { status: 403 });
export const CreateInspectionQuestionnaireLinkPayload = S.Struct({
    inspection_id: S.UUID,
    client_id: S.UUID,
    questionnaire_translation_group_id: S.Int,
});
export const CreateInspectionQuestionnaireLink = HttpApiEndpoint.post("createInspectionQuestionnaireLink", "/admin/inspections/questionnaire-links/create")
    .setPayload(CreateInspectionQuestionnaireLinkPayload)
    .addSuccess(S.Struct({ data: InspectionQuestionnaireLinkRow }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * Idempotent bulk-link, matching the sync engine's "link every cached
 * questionnaire to this inspection" flush — an id already linked is
 * skipped, never duplicated (the table's own unique constraint would
 * otherwise turn a retried batch into a partial failure).
 */
export const BatchCreateInspectionQuestionnaireLinksPayload = S.Struct({
    inspection_id: S.UUID,
    client_id: S.UUID,
    questionnaire_translation_group_ids: S.NonEmptyArray(S.Int),
});
export const BatchCreateInspectionQuestionnaireLinksResult = S.Struct({
    created: S.Int,
    skipped: S.Int,
});
export const BatchCreateInspectionQuestionnaireLinks = HttpApiEndpoint.post("batchCreateInspectionQuestionnaireLinks", "/admin/inspections/questionnaire-links/batch-create")
    .setPayload(BatchCreateInspectionQuestionnaireLinksPayload)
    .addSuccess(S.Struct({ data: BatchCreateInspectionQuestionnaireLinksResult }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteInspectionQuestionnaireLinkPayload = S.Struct({
    id: S.Int,
    client_id: S.UUID,
});
export const DeleteInspectionQuestionnaireLink = HttpApiEndpoint.post("deleteInspectionQuestionnaireLink", "/admin/inspections/questionnaire-links/delete")
    .setPayload(DeleteInspectionQuestionnaireLinkPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(InspectionQuestionnaireLinkNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// inspection_question_answers
// ---------------------------------------------------------------------------
export const InspectionAnswerRow = S.Struct({
    id: S.UUID,
    inspection_id: S.UUID,
    question_translation_group_id: S.Int,
    questionnaire_translation_group_id: S.Int,
    client_id: S.UUID,
    answer: InspectionAnswerValue,
    answered_by: S.NullOr(S.UUID),
    date_created: S.String,
    date_updated: S.String,
    version: S.Int,
});
export const InspectionAnswerVersionConflict = S.Struct({
    _tag: S.Literal("InspectionAnswerVersionConflict"),
    current: InspectionAnswerRow,
});
/**
 * A versioned update of an answer the server no longer has: the row the client
 * based its edit on was deleted, and an update never creates a row. A row
 * recreated under the same question is a different row, so an update that names
 * the old row's id gets `InspectionAnswerVersionConflict` with the new row
 * instead. The caller decides what the edit means now.
 */
export const InspectionAnswerGone = S.Struct({
    _tag: S.Literal("InspectionAnswerGone"),
});
export const ListInspectionAnswersParams = S.Struct({
    inspection_id: S.UUID,
});
export const ListInspectionAnswers = HttpApiEndpoint.get("listInspectionAnswers", "/admin/inspections/answers")
    .setUrlParams(ListInspectionAnswersParams)
    .addSuccess(S.Struct({ items: S.Array(InspectionAnswerRow) }))
    .addError(Forbidden, { status: 403 });
/**
 * Upsert on the table's own `(inspection_id, question_translation_group_id)`
 * unique constraint — the sync engine's instant-sync layer always writes
 * "the current answer to this question", never a duplicate row, so create
 * vs. update is a server-side concern the client shouldn't have to resolve
 * (it replaces the CREATE-then-fallback-to-UPDATE dance
 * `sync-engine.syncAnswer` does today).
 */
const UpsertInspectionAnswerPayloadFields = S.Struct({
    inspection_id: S.UUID,
    client_id: S.UUID,
    /**
     * The version the client's value was based on. An answer the client created
     * sends -1, "no row yet": no stored version equals it, so the conflict
     * branch of the upsert refuses the write when a row exists. A version of 0 or
     * more is an update of a row the client has seen: it never inserts, applies
     * only to the stored row with that version and that `id` (required), and is
     * refused with `InspectionAnswerGone` when the row no longer exists.
     */
    base_version: S.optional(S.Int),
    /** The id of the row the client based a versioned update on. */
    id: S.optional(S.UUID),
    question_translation_group_id: S.Int,
    questionnaire_translation_group_id: S.Int,
    answer: InspectionAnswerValue,
});
export const UpsertInspectionAnswerPayload = S.transform(UpsertInspectionAnswerPayloadFields, S.typeSchema(UpsertInspectionAnswerPayloadFields), {
    strict: true,
    decode: (payload) => ({
        ...payload,
        base_version: payload.base_version ?? (payload.id === undefined ? -1 : 0),
    }),
    encode: (payload) => payload,
}).pipe(
// A version alone does not say which row an update is based on: a row
// deleted and recreated starts at version 0 again. Below -1 means nothing.
S.filter((p) => p.base_version === undefined ||
    p.base_version === -1 ||
    (p.base_version >= 0 && p.id !== undefined) ||
    "base_version is -1 for a create, or 0 or more with the row's id for an update"));
export const UpsertInspectionAnswer = HttpApiEndpoint.post("upsertInspectionAnswer", "/admin/inspections/answers/upsert")
    .setPayload(UpsertInspectionAnswerPayload)
    .addSuccess(S.Struct({ data: InspectionAnswerRow }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(InspectionAnswerVersionConflict, { status: 409 })
    .addError(InspectionAnswerGone, { status: 409 })
    .addError(Forbidden, { status: 403 });
export const DeleteInspectionAnswerPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
});
export const DeleteInspectionAnswer = HttpApiEndpoint.post("deleteInspectionAnswer", "/admin/inspections/answers/delete")
    .setPayload(DeleteInspectionAnswerPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Boolean }) }))
    .addError(InspectionAnswerNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// inspection_answer_risks
// ---------------------------------------------------------------------------
export const InspectionAnswerRiskPhoto = S.Struct({
    file_id: S.optional(S.UUID),
    filename: S.optional(S.String),
    risk_instance_attachment_id: S.optional(S.Int),
});
export const InspectionAnswerRiskRow = S.Struct({
    id: S.Int,
    answer_id: S.UUID,
    risk_instance_translation_group_id: S.Int,
    comment: S.NullOr(S.String),
    photos: S.NullOr(S.Array(InspectionAnswerRiskPhoto)),
    date_created: S.String,
    version: S.Int,
});
export const InspectionAnswerRiskVersionConflict = S.Struct({
    _tag: S.Literal("InspectionAnswerRiskVersionConflict"),
    current: InspectionAnswerRiskRow,
});
/** As `InspectionAnswerGone`, for an answer-risk link. */
export const InspectionAnswerRiskGone = S.Struct({
    _tag: S.Literal("InspectionAnswerRiskGone"),
});
/**
 * Scoped by `inspection_id` (joined through `inspection_question_answers`)
 * rather than `answer_id` — the full-refresh/checkForServerChanges pull
 * needs every risk link for an inspection in one call, not one per answer.
 */
export const ListInspectionAnswerRisksParams = S.Struct({
    inspection_id: S.UUID,
});
export const ListInspectionAnswerRisks = HttpApiEndpoint.get("listInspectionAnswerRisks", "/admin/inspections/answer-risks")
    .setUrlParams(ListInspectionAnswerRisksParams)
    .addSuccess(S.Struct({ items: S.Array(InspectionAnswerRiskRow) }))
    .addError(Forbidden, { status: 403 });
/**
 * Upsert on `(answer_id, risk_instance_translation_group_id)` — same
 * create-vs-update relief `upsertInspectionAnswer` gives, needed here
 * because `sync-engine.syncAnswerRisk` re-links the same answer/risk pair
 * every time photos are added to it.
 */
const UpsertInspectionAnswerRiskPayloadFields = S.Struct({
    answer_id: S.UUID,
    client_id: S.UUID,
    /**
     * As on `UpsertInspectionAnswerPayload`: -1 for a link the client created; 0
     * or more is an update of a link the client has seen, which never inserts.
     */
    base_version: S.optional(S.Int),
    /** The id of the link the client based a versioned update on. */
    id: S.optional(S.Int),
    risk_instance_translation_group_id: S.Int,
    comment: S.optional(S.NullOr(S.String)),
    photos: S.optional(S.NullOr(S.Array(InspectionAnswerRiskPhoto))),
});
export const UpsertInspectionAnswerRiskPayload = S.transform(UpsertInspectionAnswerRiskPayloadFields, S.typeSchema(UpsertInspectionAnswerRiskPayloadFields), {
    strict: true,
    decode: (payload) => ({
        ...payload,
        base_version: payload.base_version ?? (payload.id === undefined ? -1 : 0),
    }),
    encode: (payload) => payload,
}).pipe(S.filter((p) => p.base_version === undefined ||
    p.base_version === -1 ||
    (p.base_version >= 0 && p.id !== undefined) ||
    "base_version is -1 for a create, or 0 or more with the link's id for an update"));
export const UpsertInspectionAnswerRisk = HttpApiEndpoint.post("upsertInspectionAnswerRisk", "/admin/inspections/answer-risks/upsert")
    .setPayload(UpsertInspectionAnswerRiskPayload)
    .addSuccess(S.Struct({ data: InspectionAnswerRiskRow }))
    .addError(InspectionAnswerNotFound, { status: 404 })
    .addError(InspectionAnswerRiskVersionConflict, { status: 409 })
    .addError(InspectionAnswerRiskGone, { status: 409 })
    .addError(Forbidden, { status: 403 });
export const DeleteInspectionAnswerRiskPayload = S.Struct({
    id: S.Int,
    client_id: S.UUID,
});
export const DeleteInspectionAnswerRisk = HttpApiEndpoint.post("deleteInspectionAnswerRisk", "/admin/inspections/answer-risks/delete")
    .setPayload(DeleteInspectionAnswerRiskPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(InspectionAnswerRiskNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// inspection_risks — a risk recorded on the inspection itself, for a finding
// that fits none of its questions. Beside `inspection_answer_risks`, which
// stays the link for a risk recorded on an answer.
// ---------------------------------------------------------------------------
export const InspectionRiskNotFound = S.Struct({
    _tag: S.Literal("InspectionRiskNotFound"),
});
/** No risk of the inspection's client belongs to the translation group. */
export const RiskGroupNotFound = S.Struct({
    _tag: S.Literal("RiskGroupNotFound"),
});
export const InspectionRiskRow = S.Struct({
    id: S.Int,
    inspection_id: S.UUID,
    risk_instance_translation_group_id: S.Int,
    comment: S.NullOr(S.String),
    photos: S.NullOr(S.Array(InspectionAnswerRiskPhoto)),
    date_created: S.String,
    version: S.Int,
});
export const InspectionRiskVersionConflict = S.Struct({
    _tag: S.Literal("InspectionRiskVersionConflict"),
    current: InspectionRiskRow,
});
/** As `InspectionAnswerRiskGone`, for an inspection-anchored risk. */
export const InspectionRiskGone = S.Struct({
    _tag: S.Literal("InspectionRiskGone"),
});
export const ListInspectionRisksParams = S.Struct({
    inspection_id: S.UUID,
});
export const ListInspectionRisks = HttpApiEndpoint.get("listInspectionRisks", "/admin/inspections/risks")
    .setUrlParams(ListInspectionRisksParams)
    .addSuccess(S.Struct({ items: S.Array(InspectionRiskRow) }))
    .addError(Forbidden, { status: 403 });
const UpsertInspectionRiskPayloadFields = S.Struct({
    inspection_id: S.UUID,
    client_id: S.UUID,
    /** As on `UpsertInspectionAnswerRiskPayload`: -1 creates, 0 or more updates. */
    base_version: S.optional(S.Int),
    /** The id of the link the client based a versioned update on. */
    id: S.optional(S.Int),
    risk_instance_translation_group_id: S.Int,
    comment: S.optional(S.NullOr(S.String)),
    photos: S.optional(S.NullOr(S.Array(InspectionAnswerRiskPhoto))),
});
export const UpsertInspectionRiskPayload = S.transform(UpsertInspectionRiskPayloadFields, S.typeSchema(UpsertInspectionRiskPayloadFields), {
    strict: true,
    decode: (payload) => ({
        ...payload,
        base_version: payload.base_version ?? (payload.id === undefined ? -1 : 0),
    }),
    encode: (payload) => payload,
}).pipe(S.filter((p) => p.base_version === undefined ||
    p.base_version === -1 ||
    (p.base_version >= 0 && p.id !== undefined) ||
    "base_version is -1 for a create, or 0 or more with the link's id for an update"));
export const UpsertInspectionRisk = HttpApiEndpoint.post("upsertInspectionRisk", "/admin/inspections/risks/upsert")
    .setPayload(UpsertInspectionRiskPayload)
    .addSuccess(S.Struct({ data: InspectionRiskRow }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(RiskGroupNotFound, { status: 404 })
    .addError(InspectionRiskVersionConflict, { status: 409 })
    .addError(InspectionRiskGone, { status: 409 })
    .addError(Forbidden, { status: 403 });
export const DeleteInspectionRiskPayload = S.Struct({
    id: S.Int,
    client_id: S.UUID,
});
export const DeleteInspectionRisk = HttpApiEndpoint.post("deleteInspectionRisk", "/admin/inspections/risks/delete")
    .setPayload(DeleteInspectionRiskPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(InspectionRiskNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// inspection detail — the composed read model behind the questions screens:
// the inspection row plus its client/location/creator display fields and the
// full questionnaire → question → answer → answer-risk tree in the
// inspection's own language.
// ---------------------------------------------------------------------------
export const InspectionDetailClient = S.Struct({
    id: S.UUID,
    name: S.NullOr(S.String),
    company_name: S.NullOr(S.String),
});
export const InspectionDetailLocation = S.Struct({
    id: S.UUID,
    name: S.String,
    key: S.NullOr(S.String),
});
export const InspectionDetailCreator = S.Struct({
    id: S.UUID,
    email: S.String,
    first_name: S.NullOr(S.String),
    last_name: S.NullOr(S.String),
});
/**
 * A risk template linked to the question via
 * `question_risk_template_links`, resolved in the inspection's language —
 * the "potential risks" hint shown while answering.
 */
export const InspectionDetailQuestionRiskTemplate = S.Struct({
    risk_template_id: S.UUID,
    risk_template_translation_group_id: S.Int,
    title: S.String,
});
/**
 * The caller's answer to one question, with every
 * `inspection_answer_risks` row hanging off it. `null` on the question
 * means unanswered.
 */
export const InspectionDetailAnswer = S.Struct({
    id: S.UUID,
    answer: InspectionAnswerValue,
    answered_by: S.NullOr(S.UUID),
    answered_at: S.String,
    risks: S.Array(InspectionAnswerRiskRow),
});
export const InspectionDetailQuestion = S.Struct({
    id: S.UUID,
    translation_group_id: S.Int,
    title: S.String,
    description: S.NullOr(S.String),
    /** `questionnaire_types.value` in the inspection's language. */
    type: S.NullOr(S.String),
    /** `questionnaire_industries.value` in the inspection's language. */
    industry: S.NullOr(S.String),
    sort_order: S.NullOr(S.Int),
    risk_templates: S.Array(InspectionDetailQuestionRiskTemplate),
    answer: S.NullOr(InspectionDetailAnswer),
});
export const InspectionDetailQuestionnaire = S.Struct({
    id: S.UUID,
    translation_group_id: S.Int,
    title: S.String,
    description: S.NullOr(S.String),
    type: S.NullOr(S.String),
    questions: S.Array(InspectionDetailQuestion),
    total_questions: S.Int,
    answered_questions: S.Int,
});
export const InspectionDetail = S.Struct({
    inspection: OnsiteInspectionRow,
    client: S.NullOr(InspectionDetailClient),
    location: S.NullOr(InspectionDetailLocation),
    creator: S.NullOr(InspectionDetailCreator),
    questionnaires: S.Array(InspectionDetailQuestionnaire),
    /** Distinct linked questions across all questionnaires. */
    total_questions: S.Int,
    total_answered_questions: S.Int,
});
export const GetInspectionDetailParams = S.Struct({ id: S.UUID });
export const GetInspectionDetail = HttpApiEndpoint.get("getInspectionDetail", "/admin/inspections/detail")
    .setUrlParams(GetInspectionDetailParams)
    .addSuccess(S.Struct({ data: InspectionDetail }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// the caller's own inspections — the mobile list screen's read model. Rows
// are always scoped to `created_by = principal.userId` server-side; the
// caller only picks the tab (drafts vs completed/submitted), a search term
// and the page.
// ---------------------------------------------------------------------------
export const MyInspectionsStatusGroup = S.Literal("drafts", "completed");
export const MyInspectionListRow = S.Struct({
    ...OnsiteInspectionRow.fields,
    client: S.NullOr(InspectionDetailClient),
    location: S.NullOr(InspectionDetailLocation),
    creator: S.NullOr(InspectionDetailCreator),
    /** Answered/total across the inspection's linked questionnaires, 0-100. */
    progress: S.Int,
    total_questions: S.Int,
    total_answered_questions: S.Int,
});
export const ListMyInspectionsParams = S.extend(PagingParams, S.Struct({
    status_group: MyInspectionsStatusGroup,
    search: S.optional(S.String),
}));
export const ListMyInspections = HttpApiEndpoint.get("listMyInspections", "/admin/inspections/mine")
    .setUrlParams(ListMyInspectionsParams)
    .addSuccess(pagedResponse(MyInspectionListRow))
    .addError(Forbidden, { status: 403 });
/**
 * Deletes ONE inspection row by id (the FK cascade takes its answers,
 * answer-risks and questionnaire links). Needed for rows without a
 * `translation_group_id` — the list screen's create-rollback and the
 * mobile delete flow — where the group-scoped delete has nothing to key on.
 */
export const DeleteInspectionPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
});
export const DeleteInspection = HttpApiEndpoint.post("deleteInspection", "/admin/inspections/delete")
    .setPayload(DeleteInspectionPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// report share — submits the inspection and schedules its report; the
// business-hours scheduling, submit dedup and risk assignment live in
// domain-data's `submitInspectionReport`.
// ---------------------------------------------------------------------------
export const InspectionDataNotSynced = S.Struct({
    _tag: S.Literal("InspectionDataNotSynced"),
    expected_risk_links: S.Int,
    actual_risk_links: S.Int,
});
/**
 * The server-computed guard: an eligible photo (a synced answer_risk's
 * `photos[].file_id`) that no risk_instance of its translation group has
 * attached yet. Runs even when the client sends no `expected_risk_links`,
 * so an old client can't bypass it. Distinct from `InspectionDataNotSynced`
 * because the recovery differs — this heals via an attachment-link repair,
 * never an answer_risk requeue.
 */
export const InspectionPhotosNotSynced = S.Struct({
    _tag: S.Literal("InspectionPhotosNotSynced"),
    photos_without_attachment: S.Int,
    findings_without_risk_instance: S.Int,
});
/**
 * A `not_ok` answer with no Mangel attached — the inspector opened the risk
 * dialog and left without saving one. No resync heals this; the caller has
 * to send the inspector back to the question.
 */
export const AnswerMissingMangel = S.Struct({
    _tag: S.Literal("AnswerMissingMangel"),
    orphaned_answer_count: S.Int,
    orphaned_question_translation_group_ids: S.Array(S.Int),
});
export const ShareInspectionReportPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    inspection_end_date: S.optional(PlainDateFromString),
    end_time: S.optional(S.String.pipe(S.pattern(/^\d{2}:\d{2}:\d{2}$/))),
    /**
     * The count of `inspection_answer_risks` rows the client's own IndexedDB
     * expects to have synced by now — guards against submitting while a
     * flaky reconnection is still draining the sync queue.
     */
    expected_risk_links: S.optional(S.Int.pipe(S.nonNegative())),
    /**
     * Photos the client gave up uploading before submit (visible to the
     * inspector as a toast) — recorded on the inspection so the generated
     * report can note them.
     */
    unuploaded_photo_count: S.optional(S.Int.pipe(S.nonNegative())),
});
export const ShareInspectionReportResult = S.Struct({
    inspection_id: S.UUID,
    status: S.Literal("submitted"),
    scheduled_time: S.String,
    already_scheduled: S.Boolean,
});
export const ShareInspectionReport = HttpApiEndpoint.post("shareInspectionReport", "/admin/inspections/report/share")
    .setPayload(ShareInspectionReportPayload)
    .addSuccess(S.Struct({ data: ShareInspectionReportResult }))
    .addError(InspectionNotFound, { status: 404 })
    .addError(InspectionDataNotSynced, { status: 409 })
    .addError(InspectionPhotosNotSynced, { status: 409 })
    .addError(AnswerMissingMangel, { status: 409 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// report regeneration — replaces the stored reports of a client's submitted
// inspections. Inspection days are UTC calendar days, both ends included.
// ---------------------------------------------------------------------------
export const InspectionReportRegenerationFilters = S.Struct({
    client_id: S.UUID,
    questionnaire_translation_group_id: S.optional(S.Int),
    date_from: S.optional(PlainDateFromString),
    date_to: S.optional(PlainDateFromString),
    /**
     * Only inspections whose shared reports still carry the pre-PMO-9
     * fallback file name, so a re-run touches only what is still broken.
     */
    affected_only: S.optional(S.Boolean),
});
export const RegenerateInspectionReportsPayload = S.Struct({
    ...InspectionReportRegenerationFilters.fields,
    /** Re-share the corrected files without emailing the customer. */
    suppress_delivery: S.optional(S.Boolean),
});
export const NoInspectionsToRegenerate = S.Struct({
    _tag: S.Literal("NoInspectionsToRegenerate"),
});
export const RegenerateInspectionReports = HttpApiEndpoint.post("regenerateInspectionReports", "/admin/inspections/reports/regenerate")
    .setPayload(RegenerateInspectionReportsPayload)
    .addSuccess(S.Struct({
    data: S.Struct({
        batch_job_id: S.UUID,
        total_inspections: S.Int,
        status: S.Literal("pending"),
    }),
}))
    .addError(NoInspectionsToRegenerate, { status: 400 })
    .addError(Forbidden, { status: 403 });
export const PreviewInspectionReportRegeneration = HttpApiEndpoint.post("previewInspectionReportRegeneration", "/admin/inspections/reports/regenerate/preview")
    .setPayload(InspectionReportRegenerationFilters)
    .addSuccess(S.Struct({
    data: S.Struct({
        regenerated_count: S.Int,
        inspections_processed: S.Array(S.UUID),
        questionnaires_generated: S.Int,
        files_deleted: S.Int,
        files_created: S.Int,
    }),
}))
    .addError(Forbidden, { status: 403 });
export const InspectionReportRegenerationNotFound = S.Struct({
    _tag: S.Literal("InspectionReportRegenerationNotFound"),
});
export const GetInspectionReportRegeneration = HttpApiEndpoint.get("getInspectionReportRegeneration", "/admin/inspections/reports/regeneration")
    .setUrlParams(S.Struct({ batch_job_id: S.UUID }))
    .addSuccess(S.Struct({
    data: S.Struct({
        batch_job_id: S.UUID,
        status: S.Literal("pending", "processing", "completed"),
        progress: S.Struct({
            total: S.Int,
            processed: S.Int,
            failed: S.Int,
            percentage: S.Int,
        }),
        failed_inspections: S.Array(S.Struct({ id: S.UUID, error: S.String })),
        created_at: S.String,
        started_at: S.optional(S.String),
        completed_at: S.optional(S.String),
    }),
}))
    .addError(InspectionReportRegenerationNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// updated-since pull — serves the sync engine's `checkForServerChanges` and
// `performInitialSync`: every inspection touched at or after a watermark,
// across every client the caller can act in. The client set is derived from
// `Principal` server-side (unrestricted internal roles see every client;
// `external-sifa` sees only `assignedClientIds`) — the endpoint takes no
// `client_id` param, so a caller cannot widen its own scope by supplying one.
// ---------------------------------------------------------------------------
export const ListInspectionsUpdatedSinceParams = S.Struct({
    since: InstantFromString,
});
export const ListInspectionsUpdatedSince = HttpApiEndpoint.get("listInspectionsUpdatedSince", "/admin/inspections/updated-since")
    .setUrlParams(ListInspectionsUpdatedSinceParams)
    .addSuccess(S.Struct({ items: S.Array(OnsiteInspectionRow) }))
    .addError(Forbidden, { status: 403 });
export const adminInspectionsGroup = HttpApiGroup.make("admin-inspections")
    .add(ListInspections)
    .add(ListInspectionsUpdatedSince)
    .add(GetInspection)
    .add(UploadInspectionPhoto)
    .add(GetInspectionDetail)
    .add(ListMyInspections)
    .add(DeleteInspection)
    .add(CreateInspection)
    .add(UpdateInspection)
    .add(DeleteInspectionsByTranslationGroup)
    .add(ListInspectionQuestionnaireLinks)
    .add(CreateInspectionQuestionnaireLink)
    .add(BatchCreateInspectionQuestionnaireLinks)
    .add(DeleteInspectionQuestionnaireLink)
    .add(ListInspectionAnswers)
    .add(UpsertInspectionAnswer)
    .add(DeleteInspectionAnswer)
    .add(ListInspectionAnswerRisks)
    .add(UpsertInspectionAnswerRisk)
    .add(DeleteInspectionAnswerRisk)
    .add(ListInspectionRisks)
    .add(UpsertInspectionRisk)
    .add(DeleteInspectionRisk)
    .add(ShareInspectionReport)
    .add(RegenerateInspectionReports)
    .add(PreviewInspectionReportRegeneration)
    .add(GetInspectionReportRegeneration)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-inspections.js.map